import { gql } from "@apollo/client";
import { alpha, LinearProgress } from "@mui/material";
import { ButtonBase, darken, lighten } from "@mui/material";
import { COLOR_POTW_PRIMARY, COLOR_PRIMARY_DARKER, COLOR_WHITE } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import React, { useEffect, useRef } from "react";

export const PotwEntryButtonFragment = gql`
  fragment PotwEntryButtonFragment on PotwEntry {
    id
    rank
    title
    subtitle
    teamName
    eventName
  }
`;

export default function PotwEntryButton({ potwEntry, active = false, playProgress = null, ...others }) {
  const ref = useRef();
  useEffect(() => {
    if (active && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [active]);

  return (
    <ButtonBase
      ref={ref}
      {...others}
      style={{
        display: "flex",
        flexFlow: "row nowrap",
        alignItems: "center",
        gap: potwEntry?.rank <= 5 ? 10 : 16,
        padding: potwEntry?.rank <= 5 ? "12px 20px" : "12px 25px",
        cursor: "pointer",
        borderBottom: `1px solid ${alpha(COLOR_WHITE, 0.1)}`,
        fontFamily: FONT_FAMILY_GRAPHIK,
        backgroundColor: active ? lighten(COLOR_PRIMARY_DARKER, 0.1) : COLOR_PRIMARY_DARKER,
        ...(potwEntry?.rank <= 5 && {
          backgroundColor: active ? lighten(COLOR_POTW_PRIMARY, 0.1) : darken(COLOR_POTW_PRIMARY, 0.2),
        }),
        position: "relative",
        ...others.style,
      }}
    >
      <div
        style={{
          fontStyle: "italic",
        }}
      >
        <span style={{ opacity: 0.6 }}>{"#"}</span>
        <span
          style={{
            opacity: 0.7,
            fontSize: "1.2em",
            fontFamily: FONT_FAMILY_GRAPHIK,
            ...(potwEntry?.rank <= 5 && {
              opacity: 1,
              fontSize: "1.6em",
              padding: "0 5px",
            }),
          }}
        >
          {potwEntry?.rank}
        </span>
      </div>
      <div
        style={{
          flex: "1 1 200px",
          display: "flex",
          flexFlow: "column nowrap",
          alignItems: "flex-start",
          textAlign: "left",
          fontFamily: FONT_FAMILY_GRAPHIK,
          gap: 5,
        }}
      >
        <div style={{ ...(potwEntry?.rank <= 5 && { fontWeight: 500, fontSize: "0.85rem" }) }}>{potwEntry.title}</div>
        <div
          style={{
            fontSize: ".8em",
            fontWeight: 500,
            fontFamily: FONT_FAMILY_GRAPHIK,
            textTransform: "uppercase",
            opacity: 0.8,
          }}
        >
          {potwEntry?.eventName}
        </div>
        <div
          style={{
            fontSize: ".8em",
            fontFamily: FONT_FAMILY_GRAPHIK,
            opacity: 0.8,
            fontStyle: "italic",
          }}
        >
          {potwEntry?.teamName}
        </div>
        <div
          style={{
            fontSize: ".8em",
            fontFamily: FONT_FAMILY_GRAPHIK,
            opacity: 0.7,
          }}
        >
          {potwEntry.subtitle}
        </div>
      </div>
      {playProgress !== null && !Number.isNaN(playProgress) && (
        <LinearProgress
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            right: 0,
          }}
          color="inherit"
          variant="determinate"
          value={playProgress * 100}
        />
      )}
    </ButtonBase>
  );
}
